import {
  DEFAULT_RECEIPT_UPLOAD_CONCURRENCY,
  type QueuedReceiptUpload,
  type ReceiptUploadOutcome,
  receiptsActions,
} from 'app-components/flux/receipts';
import { ConcurrentTaskQueue } from 'app-components/utils/async';

import type { AppDispatch } from 'store';
import { releaseLocalReceiptFile } from 'utils/receiptLocalPreview';

import { stageReceiptReupload, type UploadReceiptInput, uploadReceiptFromFile } from './uploadReceipt';

type ReceiptUploadTask = {
  dispatch: AppDispatch;
  upload: QueuedReceiptUpload;
  releaseFileUri?: () => void;
};

type ReuploadReceiptInput = {
  dispatch: AppDispatch;
  localId: string;
  fileUri: string;
  fileName: string;
  mimeType: string;
  releaseFileUri?: () => void;
};

export type ReceiptUploadCoordinator = {
  enqueue: (
    dispatch: AppDispatch,
    uploads: ReadonlyArray<QueuedReceiptUpload>,
  ) => ReadonlyArray<Promise<ReceiptUploadOutcome>>;
  reupload: (input: ReuploadReceiptInput) => Promise<ReceiptUploadOutcome>;
  cancel: (localId: string) => boolean;
  invalidate: () => void;
  /** Local ids whose upload is in flight, so the local upload deadline can skip them. */
  getActiveUploadIds: () => ReadonlyArray<string>;
};

type CreateReceiptUploadCoordinatorOptions = {
  concurrency?: number;
  upload?: (input: UploadReceiptInput) => Promise<void>;
};

const releaseOnce = (release?: () => void): (() => void) | undefined => {
  if (!release) {
    return undefined;
  }
  let released = false;
  return (): void => {
    if (!released) {
      released = true;
      release();
    }
  };
};

export const createReceiptUploadCoordinator = ({
  concurrency = DEFAULT_RECEIPT_UPLOAD_CONCURRENCY,
  upload = uploadReceiptFromFile,
}: CreateReceiptUploadCoordinatorOptions = {}): ReceiptUploadCoordinator => {
  const queue = new ConcurrentTaskQueue<ReceiptUploadTask>(
    async ({ dispatch, upload: queuedUpload, releaseFileUri }, signal): Promise<void> => {
      await upload({
        dispatch,
        localId: queuedUpload.localId,
        fileUri: queuedUpload.fileUri,
        fileName: queuedUpload.fileName,
        mimeType: queuedUpload.mimeType,
        pendingReceiptAlreadyAdded: true,
        releaseFileUri,
        abortSignal: signal,
      });
    },
    concurrency,
  );
  const uploadsByLocalId = new Map<string, Promise<ReceiptUploadOutcome>>();

  const enqueueTask = (task: ReceiptUploadTask): Promise<ReceiptUploadOutcome> => {
    const { localId } = task.upload;
    const existingUpload = uploadsByLocalId.get(localId);
    if (existingUpload) {
      return existingUpload;
    }

    const [result] = queue.enqueue([task]);
    if (!result) {
      throw new Error('Failed to enqueue receipt upload.');
    }

    const trackedResult = result.finally(() => uploadsByLocalId.delete(localId));
    uploadsByLocalId.set(localId, trackedResult);
    return trackedResult;
  };

  const enqueue = (
    dispatch: AppDispatch,
    uploads: ReadonlyArray<QueuedReceiptUpload>,
  ): ReadonlyArray<Promise<ReceiptUploadOutcome>> =>
    uploads.map((queuedUpload) =>
      enqueueTask({
        dispatch,
        upload: queuedUpload,
        releaseFileUri: releaseOnce(
          queuedUpload.fileUri.startsWith('blob:') ? () => releaseLocalReceiptFile(queuedUpload.fileUri) : undefined,
        ),
      }),
    );

  return {
    enqueue,
    reupload: ({ dispatch, localId, fileUri, fileName, mimeType, releaseFileUri }): Promise<ReceiptUploadOutcome> => {
      const existingUpload = uploadsByLocalId.get(localId);
      if (existingUpload) {
        return existingUpload;
      }

      const queuedUpload = stageReceiptReupload({ dispatch, localId, fileUri, fileName, mimeType });
      return enqueueTask({ dispatch, upload: queuedUpload, releaseFileUri: releaseOnce(releaseFileUri) });
    },
    cancel: (localId): boolean => {
      const canceled = queue.cancel(({ upload: queuedUpload }) => queuedUpload.localId === localId);
      canceled.forEach(({ releaseFileUri }) => releaseFileUri?.());
      return canceled.length > 0;
    },
    invalidate: (): void => {
      queue.invalidate().forEach(({ dispatch, upload: queuedUpload, releaseFileUri }) => {
        dispatch(receiptsActions.removeReceipt(queuedUpload.localId));
        releaseFileUri?.();
      });
    },
    getActiveUploadIds: (): ReadonlyArray<string> =>
      queue.getActiveItems().map(({ upload: queuedUpload }) => queuedUpload.localId),
  };
};

export const receiptUploadCoordinator = createReceiptUploadCoordinator();
